import type { Attempt } from '../infrastructure/indexeddb/PersonalStore';
import { themeLabel } from '../domain/puzzle/catalog';
import { RecentPractice } from './RecentPractice';
import { SessionStrip } from './SessionStrip';
import { Scoresheet } from './Scoresheet';
import { useLayoutEffect, useRef, useState, type ComponentProps } from 'react';
import type { allTimeStats } from './useSessionStats';

export function StatsPage({
  history,
  recent,
  stats,
  allTime,
  reduced,
  resume,
  back,
  exact,
  choose,
  scores,
}: {
  history: Attempt[];
  recent: ComponentProps<typeof RecentPractice>['stats'] | null;
  stats: ComponentProps<typeof SessionStrip>['stats'];
  allTime?: ReturnType<typeof allTimeStats>;
  reduced: boolean;
  resume?: () => void;
  back: () => void;
  exact: (id: string) => void;
  choose: () => void;
  scores?: ComponentProps<typeof Scoresheet>;
}) {
  const [visible, setVisible] = useState(100);
  const list = useRef<HTMLDivElement>(null);
  const firstNew = useRef<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (firstNew.current === undefined) return;
    list.current?.querySelectorAll('button')[firstNew.current]?.focus();
    firstNew.current = undefined;
  }, [visible]);
  return (
    <main className="stats-page" aria-label="Your practice">
      <header className="stats-heading">
        <button onClick={back}>‹ Back</button>
        <h1>Stats</h1>
      </header>
      <div className="stats-columns">
        <section className="stats-summary" aria-label="Practice summary">
          {allTime && allTime.days > 0 && (
            <>
              <h2>All time</h2>
              <dl className="session-strip all-time-strip" aria-label="All-time practice">
                <div title="Tactics solved, excluding revealed solutions.">
                  <dt>Solved</dt>
                  <dd data-testid="all-solved">{allTime.solved}</dd>
                </div>
                <div title="Finished tactics solved without mistakes or hints.">
                  <dt>First try</dt>
                  <dd data-testid="all-first-try">
                    {allTime.firstTry === null ? '—' : `${allTime.firstTry}%`}
                  </dd>
                </div>
                <div title="Tactics solved in the last seven days.">
                  <dt>This week</dt>
                  <dd data-testid="all-week">{allTime.week}</dd>
                </div>
                <div title="Days with at least one finished puzzle.">
                  <dt>Days practised</dt>
                  <dd data-testid="all-days">{allTime.days}</dd>
                </div>
              </dl>
            </>
          )}
          {recent && (
            <RecentPractice
              stats={recent}
              resume={resume}
              viewHistory={() =>
                document.querySelector('#practice-history')?.scrollIntoView({ block: 'start' })
              }
            />
          )}
          <h2>This session</h2>
          <SessionStrip stats={stats} reduced={reduced} />
          {scores && <Scoresheet {...scores} />}
        </section>
        <section id="practice-history" className="stats-history" aria-label="Practice history">
          <h2>Your practice</h2>
          <p className="muted">
            A record of your ideas, misses, and good moves. Select a position to revisit it.
          </p>
          <div className="history-list" id="practice-attempts" ref={list}>
            {history.length ? (
              history.slice(0, visible).map((a) => (
                <button key={a.id} onClick={() => exact(a.puzzleId)}>
                  <span>
                    <strong>#{a.puzzleId}</strong>
                    <small>
                      {a.rating} difficulty · {new Date(a.completedAt).toLocaleDateString()}
                    </small>
                  </span>
                  <span className={a.solvedFirstAttempt ? 'success-text' : ''}>
                    {a.solvedFirstAttempt
                      ? '✓ Clean solve'
                      : a.outcome === 'solved'
                        ? '✓ Solved'
                        : themeLabel(a.outcome)}
                  </span>
                </button>
              ))
            ) : (
              <div className="empty-practice">
                <img src="/design/brand/practice-path.svg" alt="" width="112" height="112" />
                <p>Your first puzzle starts the story.</p>
              </div>
            )}
          </div>
          {history.length > 100 && (
            <div className="history-pagination">
              <p className="muted" role="status">
                Showing {Math.min(visible, history.length)} of {history.length} attempts.
              </p>
              {visible < history.length && (
                <button
                  aria-controls="practice-attempts"
                  onClick={() => {
                    firstNew.current = visible;
                    setVisible((count) => count + 100);
                  }}
                >
                  Show older attempts
                </button>
              )}
            </div>
          )}
          <button onClick={choose}>Choose practice</button>
        </section>
      </div>
    </main>
  );
}
